/**
 * Impersonation-aware session access.
 *
 * The client portal supports admin impersonation: the admin tab posts a
 * client-scoped token which is stored in `sessionStorage.impersonation` as
 * `{ token, user, impersonatedBy }`. `localStorage` still holds the ADMIN's
 * token and cached user throughout.
 *
 * So any portal screen that reads `localStorage.getItem('token')` or
 * `localStorage.getItem('user')` directly is wrong under impersonation — it
 * will act as the admin while the UI claims to be the client. `ClientProfile`
 * did exactly this on both reads and writes, so saving a profile while
 * impersonating wrote the admin's own name, phone and marketing opt-out.
 * That last field is the CAN-SPAM record, which made it a compliance bug
 * rather than a cosmetic one.
 *
 * `getActiveToken()` was previously a private function inside ClientPortal.jsx,
 * which is why the other portal screens couldn't use it. It lives here now.
 * Lessons 12 and 25: in the portal, identity and token must BOTH come from the
 * impersonation-aware source.
 */

function readImpersonation() {
  try {
    const raw = sessionStorage.getItem('impersonation');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.token ? parsed : null;
  } catch (e) {
    return null;
  }
}

/** Token for API calls — impersonation wins over normal login. */
export function getActiveToken() {
  const imp = readImpersonation();
  if (imp) return imp.token;
  return localStorage.getItem('token');
}

/** The user the screen is acting AS — not necessarily the logged-in user. */
export function getActiveUser() {
  const imp = readImpersonation();
  if (imp && imp.user) return imp.user;
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch (e) {
    return {};
  }
}

/** True when viewing as someone else. Screens should not let you edit their profile. */
export function isImpersonating() {
  return !!readImpersonation();
}

/** Ready-made axios config. */
export function authHeaders() {
  return { headers: { Authorization: `Bearer ${getActiveToken()}` } };
}

export default { getActiveToken, getActiveUser, isImpersonating, authHeaders };
